import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { autoSelectLargestField, formatNumber, useFieldGeometry, useGetFieldsByFarmId, useInView, useTranslation, useUserContext } from "utils";

import { SOIL_SIGHT_DRAWER_WIDTH } from "consts";

import { FieldListField } from "models";

import { Drawer, ErrorBoundary, Icon, InputSearch, MapStatic, Skeleton, Typography } from "components";

import { FieldUpload } from "pages/app/fields/components/FieldUpload";

export type FieldMapIconProps = {
  field: FieldListField;
  /** Icon-box class; the map variant adds a `--map` modifier on top of it. */
  className: string;
  fallback?: React.ReactNode;
};

/** Static map of the field's shape, falling back to a glyph until a shape is available. */
export const FieldMapIcon = ({ field, className, fallback }: FieldMapIconProps) => {
  const { ref, inView } = useInView({ triggerOnce: true });
  const { geometry } = useFieldGeometry(field.id, inView);

  if (!geometry || !field.location) {
    return (
      <div ref={ref} className={className}>
        {fallback ?? <Icon iconName="klim/field" size="m" />}
      </div>
    );
  }

  return (
    <div ref={ref} className={`${className} ${className}--map`}>
      <ErrorBoundary>
        {/* @ts-expect-error need to convert to TS */}
        <MapStatic field={field} geometry={geometry} location={field.location} />
      </ErrorBoundary>
    </div>
  );
};

export type FieldSelectorProps = {
  /** Farm whose fields are listed — field selection is scoped to it. */
  farmId: string | null;
  selectedFieldId: string | null;
  setSelectedFieldId: (id: string | null) => void;
  /** Off in the reselection modal, so it starts empty and forces a deliberate choice. */
  autoSelectLargest?: boolean;
  /** On in the reselection modal, where an empty farm must be resolved before continuing. */
  autoOpenAddFieldWhenEmpty?: boolean;
  onFieldAdded?: (fieldId: string) => void;
};

/**
 * The field dropdown card shared by the SoilSight onboarding and the scenario field-reselection modal.
 */
export const FieldSelector = ({
  farmId,
  selectedFieldId,
  setSelectedFieldId,
  autoSelectLargest = true,
  autoOpenAddFieldWhenEmpty = false,
  onFieldAdded,
}: FieldSelectorProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { user, switchFarm } = useUserContext();

  // FieldUpload creates on the active farm, so switch first — but only when it differs, since switchFarm
  // invalidates every farm-scoped query.
  const switchFarmIfNeeded = useCallback(
    (targetFarmId: string) => {
      if (user?.farm?.id === targetFarmId) return;
      switchFarm(targetFarmId);
    },
    [user?.farm?.id, switchFarm]
  );

  const [fieldOpen, setFieldOpen] = useState(false);
  const [fieldSearch, setFieldSearch] = useState("");
  const [showAddFieldDrawer, setShowAddFieldDrawer] = useState(false);

  const fieldRef = useRef<HTMLDivElement>(null);

  const {
    data: fields = [],
    isLoading: fieldsLoading,
    refetch: refetchFields,
  } = useGetFieldsByFarmId({
    farmId: farmId ?? "",
    enabled: !!farmId,
  });

  // Clear a selection that is no longer among the farm's fields (e.g. it was deleted).
  useEffect(() => {
    if (fieldsLoading || !selectedFieldId) return;
    if (!fields.some((f) => f.id === selectedFieldId)) setSelectedFieldId(null);
  }, [fields, fieldsLoading, selectedFieldId, setSelectedFieldId]);

  useEffect(() => {
    if (!autoSelectLargest || fieldsLoading || fields.length === 0 || selectedFieldId) return;
    const largest = autoSelectLargestField(fields);
    if (largest) setSelectedFieldId(largest.id);
  }, [autoSelectLargest, fieldsLoading, fields, selectedFieldId, setSelectedFieldId]);

  // Empty farm → open the add-field drawer once per farm.
  const addFieldAutoOpenedForFarmRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoOpenAddFieldWhenEmpty || !farmId || fieldsLoading || fields.length > 0) return;
    if (addFieldAutoOpenedForFarmRef.current === farmId) return;
    addFieldAutoOpenedForFarmRef.current = farmId;
    switchFarmIfNeeded(farmId);
    setShowAddFieldDrawer(true);
  }, [autoOpenAddFieldWhenEmpty, farmId, fieldsLoading, fields, switchFarmIfNeeded]);

  // Click-outside field dropdown
  useEffect(() => {
    if (!fieldOpen) return undefined;
    const handle = (e: MouseEvent) => {
      if (fieldRef.current && !fieldRef.current.contains(e.target as Node)) {
        setFieldOpen(false);
        setFieldSearch("");
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [fieldOpen]);

  const handleFieldSelect = useCallback(
    (fieldId: string) => {
      setSelectedFieldId(fieldId);
      setFieldOpen(false);
      setFieldSearch("");
    },
    [setSelectedFieldId]
  );

  const handleFieldAdded = useCallback(async () => {
    setShowAddFieldDrawer(false);
    const prevIds = new Set(fields.map((f: FieldListField) => f.id));
    const { data: updated } = await refetchFields();
    if (!updated || updated.length === 0) return;
    const createdField = updated.find((f: FieldListField) => !prevIds.has(f.id));
    if (createdField) onFieldAdded?.(createdField.id);
    const newField = createdField ?? autoSelectLargestField(updated);
    if (newField) setSelectedFieldId(newField.id);
  }, [fields, refetchFields, setSelectedFieldId, onFieldAdded]);

  const filteredFields = useMemo(() => fields.filter((f: FieldListField) => f.name.toLowerCase().includes(fieldSearch.toLowerCase())), [fields, fieldSearch]);
  const selectedField = useMemo(() => fields.find((f: FieldListField) => f.id === selectedFieldId) ?? null, [fields, selectedFieldId]);

  const farmHasNoFields = !!farmId && !fieldsLoading && fields.length === 0;

  const openAddFieldDrawer = useCallback(() => {
    setFieldOpen(false);
    if (farmId) switchFarmIfNeeded(farmId);
    setShowAddFieldDrawer(true);
  }, [farmId, switchFarmIfNeeded]);

  return (
    <>
      <div ref={fieldRef} className="farm-field-step__card-wrapper">
        {farmId && fieldsLoading ? (
          <Skeleton type="block" height="4.125rem" />
        ) : (
          <div
            className={`farm-field-step__card-trigger${!farmId ? " farm-field-step__card-trigger--disabled" : ""}`}
            data-testid={farmHasNoFields ? "scenario-onboarding-field-add" : "scenario-onboarding-field-select"}
            role="button"
            tabIndex={farmId ? 0 : -1}
            onClick={() => {
              if (!farmId) return;
              if (farmHasNoFields) openAddFieldDrawer();
              else setFieldOpen((prev) => !prev);
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter" || !farmId) return;
              if (farmHasNoFields) openAddFieldDrawer();
              else setFieldOpen((prev) => !prev);
            }}
          >
            {selectedField ? (
              <FieldMapIcon field={selectedField} className="farm-field-step__card-icon" />
            ) : (
              <div className="farm-field-step__card-icon">
                <Icon iconName="klim/field" size="m" />
              </div>
            )}
            <div className="farm-field-step__card-content">
              {selectedField ? (
                <>
                  <span className="farm-field-step__card-name">{selectedField.name}</span>
                  {selectedField.size > 0 && (
                    <span className="farm-field-step__card-sub">{formatNumber({ number: selectedField.size, decimalPlaces: 2 })} ha</span>
                  )}
                </>
              ) : (
                <span className="farm-field-step__card-placeholder">
                  {!farmId
                    ? t("onboarding.step1.fieldPlaceholderNoFarm")
                    : farmHasNoFields
                    ? t("onboarding.step1.addFieldCta")
                    : t("onboarding.step1.fieldPlaceholder")}
                </span>
              )}
            </div>
            {farmHasNoFields ? (
              <Icon iconName="klim/plus" size="s" className="farm-field-step__card-chevron" />
            ) : (
              <Icon iconName={fieldOpen ? "tabler/chevron-up" : "tabler/chevron-down"} size="s" className="farm-field-step__card-chevron" />
            )}
          </div>
        )}

        {fieldOpen && farmId && !farmHasNoFields && (
          <div className="farm-field-step__dropdown-panel">
            <div className="farm-field-step__dropdown-search-row">
              <InputSearch
                autoFocus
                className="farm-field-step__dropdown-search-input"
                placeholder={t("onboarding.step1.searchFieldPlaceholder")}
                value={fieldSearch}
                onChange={(e) => setFieldSearch(e.target.value)}
                allowClear={false}
                variant="borderless"
                dataTestId="scenario-onboarding-field-search"
              />
              <Icon iconName="tabler/search" size="s" className="farm-field-step__dropdown-search-icon" />
            </div>

            {filteredFields.length > 0 ? (
              <ul className="farm-field-step__dropdown-list" role="listbox">
                {filteredFields.map((field: FieldListField) => (
                  <li
                    key={field.id}
                    role="option"
                    tabIndex={0}
                    aria-selected={field.id === selectedFieldId}
                    className={`farm-field-step__dropdown-row${field.id === selectedFieldId ? " farm-field-step__dropdown-row--selected" : ""}`}
                    onClick={() => handleFieldSelect(field.id)}
                    onKeyDown={(e) => e.key === "Enter" && handleFieldSelect(field.id)}
                  >
                    <FieldMapIcon field={field} className="farm-field-step__option-icon" />
                    <div className="farm-field-step__option-body">
                      <span className="farm-field-step__option-name">{field.name}</span>
                      {field.size > 0 && <span className="farm-field-step__option-sub">{formatNumber({ number: field.size, decimalPlaces: 2 })} ha</span>}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="farm-field-step__dropdown-empty">
                <Icon iconName="klim/field" size="xl" className="farm-field-step__dropdown-empty-icon" />
                <div className="farm-field-step__dropdown-empty-text">
                  <Typography variant="paragraph-m" className="farm-field-step__dropdown-empty-title">
                    {t("onboarding.step1.noFieldsTitle")}
                  </Typography>
                  <Typography variant="paragraph-s" color="grey-600">
                    {t("onboarding.step1.noFieldsSubtitle")}
                  </Typography>
                </div>
              </div>
            )}

            <div
              className="farm-field-step__dropdown-add-row"
              role="button"
              tabIndex={0}
              onClick={openAddFieldDrawer}
              onKeyDown={(e) => {
                if (e.key === "Enter") openAddFieldDrawer();
              }}
            >
              <div className="farm-field-step__dropdown-add-icon">
                <Icon iconName="klim/plus" size="s" />
              </div>
              <span className="farm-field-step__dropdown-add-label">{t("onboarding.step1.addFieldCta")}</span>
            </div>
          </div>
        )}
      </div>

      <Drawer
        title={t("onboarding.step1.addFieldDrawerTitle")}
        onClose={() => setShowAddFieldDrawer(false)}
        width={SOIL_SIGHT_DRAWER_WIDTH}
        visible={showAddFieldDrawer}
      >
        <FieldUpload onFinish={handleFieldAdded} initialStep="MANUAL_UPLOAD" />
      </Drawer>
    </>
  );
};

export default FieldSelector;
